import { MouseEvent } from 'react';

import { CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useConfirm } from 'material-ui-confirm';

import { ReactComponent as FixarIcon } from 'assets/images/pin-icon.svg';
import { ReactComponent as DesafixarIcon } from 'assets/images/unpin-icon.svg';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  desafixarAtendimento,
  fetchAtendimentosIniciados,
  fixarAtendimento,
} from 'store/reducers/atendimentos';
import { getUrlParams } from 'utils/getUrlParams';

interface ButtonFixarAtendimentoProps {
  atendimentoId: string;
  fixed?: boolean;
}

const useStyles = makeStyles(({ palette }) => ({
  tooltip: {
    fontSize: 11,
  },
  icon: ({ fixed }: { fixed?: boolean }) => ({
    fill: fixed ? palette.primary.main : palette.action.active,
    width: 22,
    height: 22,
  }),
}));

function ButtonFixarAtendimento(props: ButtonFixarAtendimentoProps) {
  const { atendimentoId, fixed } = props;

  const classes = useStyles({ fixed });
  const confirm = useConfirm();
  const dispatch = useAppDispatch();
  const { loadingFixarAtendimento } = useAppSelector(atendimentosSelector);

  const { orderInitiatedBy } = getUrlParams();

  const title = fixed ? `Desafixar atendimento` : `Fixar atendimento`;
  const description = fixed
    ? `Ao desafixar o atendimento ele poderá retornar para fila após inatividade!`
    : `Ao fixar o atendimento ele não retornará para fila após inatividade!`;

  const onFixAtendimento = () => {
    dispatch(fixarAtendimento(atendimentoId)).then(() => {
      dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
    });
  };

  const onUnfixAtendimento = () => {
    dispatch(desafixarAtendimento(atendimentoId)).then(() => {
      dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
    });
  };

  const handleConfirm = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    confirm({
      title,
      description,
      dialogProps: { maxWidth: 'xs', fullWidth: true },
      cancellationText: 'Cancelar',
      confirmationText: 'Ok',
    })
      .then(() => {
        if (fixed) onUnfixAtendimento();
        else onFixAtendimento();
      })
      .catch(() => undefined);
  };

  return (
    <Tooltip
      arrow
      placement="top"
      title={title}
      classes={{ tooltip: classes.tooltip }}
    >
      <IconButton
        size="small"
        aria-label="pin and unpin"
        onClick={handleConfirm}
        disabled={loadingFixarAtendimento}
      >
        {loadingFixarAtendimento ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <>
            {fixed ? (
              <DesafixarIcon className={classes.icon} />
            ) : (
              <FixarIcon className={classes.icon} />
            )}
          </>
        )}
      </IconButton>
    </Tooltip>
  );
}

export default ButtonFixarAtendimento;
